import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import dayjs from 'dayjs';

export const compose = <T>(fn1: (a: T) => T, ...fns: Array<(a: T) => T>) =>
  fns.reduce((prevFn, nextFn) => (value) => prevFn(nextFn(value)), fn1);

// Inclusive bounds on the LOCAL calendar days picked: the picker is `dateOnly`, and
// `new Date('YYYY-MM-DD')` is UTC midnight, read back a day early (CUS-318).
export const convertDateRangeValues = (
  value: DateRangePickerProps.Value | null
) => {
  if (!value || !value?.type) {
    return {
      type: null,
      startDate: null,
      endDate: null,
    };
  }

  if (value.type === 'absolute') {
    const startDate = dayjs(value.startDate).startOf('day');
    const endDate = dayjs(value.endDate).endOf('day');

    return {
      startDate: startDate.isValid() ? startDate.toDate() : null,
      endDate: endDate.isValid() ? endDate.toDate() : null,
    };
  }

  const { amount, unit } = value;
  // One reference instant for both bounds: two `dayjs()` calls straddling local
  // midnight would put them on different days.
  const now = dayjs();
  const offset = now.subtract(-amount, unit);

  return amount < 0
    ? {
        startDate: offset.startOf('day').toDate(),
        endDate: now.endOf('day').toDate(),
      }
    : {
        startDate: now.startOf('day').toDate(),
        endDate: offset.endOf('day').toDate(),
      };
};

export const applyOpenInDateRangeFilter =
  (dateRange: DateRangePickerProps.Value | null) =>
  <T>(filters: T) => {
    if (!dateRange) {
      return {
        ...filters,
      };
    }
    const { startDate, endDate } = convertDateRangeValues(dateRange);
    if (!startDate || !endDate) {
      return {
        ...filters,
      };
    }

    return {
      ...filters,
      _or: [
        {
          ActualCloseDate: {
            _gte: startDate,
          },
        },
        {
          CreatedAtTimestamp: {
            _lte: endDate,
          },
        },
      ],
    };
  };
